import React from 'react';
import { FaCheckCircle, FaFileAlt, FaWhatsapp, FaShieldAlt, FaTimesCircle } from 'react-icons/fa';
import SEO from '../components/SEO';
import { OBRAS_SOCIALES, SEDES } from '../data/organizacion';
import { breadcrumb, faq } from '../data/schema';

const gtag_report_conversion = (url) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'conversion', {
      'send_to': 'AW-16956303663/7KnDCOiB6qIcEK_SspU_',
      'event_callback': function () {
        if (typeof(url) !== 'undefined') {
          window.location = url;
        }
      }
    });
  } else {
    window.location = url;
  }
};

const ObrasSociales = () => {
  const { principales, adicionales, noAtendidas } = OBRAS_SOCIALES;
  // " ni " para el negativo ("no trabajamos con A ni B"), " o " para la pregunta.
  const noAtendidasTexto = noAtendidas.join(' ni ');
  const noAtendidasPregunta = noAtendidas.join(' o ');

  const serviciosCubiertos = [
    "Centro Educativo Terapéutico (CET)",
    "Centro de Día (CD)",
    "Terapias individuales",
    "Transporte especial",
    "Alimentación",
    "Actividades terapéuticas"
  ];

  const documentosNecesarios = [
    {
      documento: "Certificado Único de Discapacidad (CUD)",
      descripcion: "Vigente y con diagnóstico actualizado"
    },
    {
      documento: "Prescripción médica",
      descripcion: "Indicando tipo de prestación (CET o CD) y cantidad de sesiones"
    },
    {
      documento: "DNI del beneficiario",
      descripcion: "Original y copia"
    },
    {
      documento: "Carnet de obra social",
      descripcion: "Vigente del titular y beneficiario"
    },
    {
      documento: "Historia clínica",
      descripcion: "Resumen actualizado del médico tratante"
    },
    {
      documento: "Informes profesionales",
      descripcion: "De terapeutas anteriores si los hubiera"
    }
  ];

  const procesoGestion = [
    {
      paso: "1",
      titulo: "Evaluación inicial",
      descripcion: "Entrevista con nuestro equipo para determinar el servicio adecuado"
    },
    {
      paso: "2",
      titulo: "Recopilación de documentos",
      descripcion: "Nuestro equipo administrativo te ayuda a reunir toda la documentación"
    },
    {
      paso: "3",
      titulo: "Presentación a obra social",
      descripcion: "Gestionamos la autorización ante tu obra social"
    },
    {
      paso: "4",
      titulo: "Inicio del tratamiento",
      descripcion: "Una vez aprobado, comenzamos con el proceso de admisión"
    }
  ];

  // Las preguntas se declaran una sola vez y alimentan tanto el render como el
  // JSON-LD: así la respuesta que cita un asistente es literalmente la que lee
  // una persona en la página.
  const preguntasFrecuentes = [
    {
      pregunta: `¿Trabajan con ${noAtendidasPregunta}?`,
      respuesta:
        `No. Mi Lugar no trabaja con ${noAtendidasTexto}. Sí trabajamos con ` +
        `${principales.map((os) => os.nombre).join(', ')} y con muchas otras obras ` +
        `sociales y prepagas. Si tu cobertura no figura en la lista, consultanos: ` +
        `evaluamos cada caso.`
    },
    {
      pregunta: "¿Qué pasa si no tengo CUD?",
      respuesta:
        "Te orientamos y acompañamos en el proceso para obtener el Certificado Único de " +
        "Discapacidad. Es un trámite gratuito y fundamental para acceder a los beneficios."
    },
    {
      pregunta: "¿Cuánto demora la autorización?",
      respuesta:
        "Generalmente entre 15 y 30 días hábiles, dependiendo de cada obra social. " +
        "Durante este tiempo, podés conocer nuestras instalaciones y equipo."
    },
    {
      pregunta: "¿Qué incluye la cobertura?",
      respuesta:
        "La cobertura incluye todas las prestaciones del Centro de Día o CET: terapias, " +
        "actividades, alimentación y, en muchos casos, también el transporte."
    },
    {
      pregunta: "¿Tengo que pagar algo extra?",
      respuesta:
        "No. Las prestaciones básicas tienen cobertura del 100% por ley (Ley 24.901). " +
        "No deberías abonar costos adicionales por los servicios incluidos."
    }
  ];

  return (
    <div className="container">
      <SEO
        title="Obras Sociales — Cobertura CET y CD | Mi Lugar"
        description={`Trabajamos con ${principales.slice(0, 5).map((os) => os.nombre).join(', ')} y muchas más. Gestionamos las autorizaciones para CET y Centro de Día sin costo extra. No trabajamos con ${noAtendidasTexto}.`}
        path="/obras-sociales"
        jsonLd={[
          breadcrumb([{ nombre: 'Obras Sociales', path: '/obras-sociales' }]),
          faq(preguntasFrecuentes, '/obras-sociales'),
        ]}
      />
      {/* Header */}
      <div className="sede-header">
        <h1>Centro de Día y CET</h1>
        <h2>Atención a Obras Sociales en Zona Sur</h2>
        <p>Trabajamos con la mayoría de las obras sociales y prepagas. Nuestro equipo gestiona las autorizaciones 
          para que puedas acceder a nuestros servicios de <strong>Centro Educativo Terapéutico</strong> y 
          <strong> Centro de Día</strong> sin complicaciones.</p>
      </div>

      {/* Introducción */}
      <section className="sede-info">
        <div className="sede-details">
          <h2>Cobertura integral para obras sociales</h2>
          <p>
            En <strong>Mi Lugar</strong>, entendemos la importancia del acceso a servicios de calidad para 
            personas con discapacidad. Por eso trabajamos con un amplio abanico de obras sociales y prepagas 
            para que cada familia pueda acceder a nuestros servicios de CET y Centro de Día.
          </p>
          <div className="facilities-grid">
            <div className="facility-item">
              <FaCheckCircle className="facility-icon" />
              <div>
                <h3>Gestión administrativa sin cargo</h3>
              </div>
            </div>
            <div className="facility-item">
              <FaCheckCircle className="facility-icon" />
              <div>
                <h3>Asesoramiento para obtener el CUD</h3>
              </div>
            </div>
            <div className="facility-item">
              <FaCheckCircle className="facility-icon" />
              <div>
                <h3>Seguimiento de autorizaciones</h3>
              </div>
            </div>
            <div className="facility-item">
              <FaCheckCircle className="facility-icon" />
              <div>
                <h3>Renovaciones automáticas</h3>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Obras Sociales */}
      <section className="sede-facilities">
        <h2 className="section-title">Principales Obras Sociales con las que Trabajamos</h2>
        <div className="facilities-grid">
          {principales.map((os) => (
            <div key={os.nombre} className="facility-item">
              <FaShieldAlt className="facility-icon" />
              <div>
                <h3>{os.nombre}</h3>
                <p><strong>Cobertura:</strong> {os.cobertura}</p>
              </div>
            </div>
          ))}
        </div>
        <p>
          * También trabajamos con: {adicionales.join(', ')}, y con muchas otras obras 
          sociales y prepagas. Si la tuya no figura acá, consultanos.
        </p>

        {/* Decir explícitamente qué NO cubrimos evita que una familia llegue con una
            expectativa equivocada, y evita que los asistentes de IA infieran cobertura
            a partir de frases generales. */}
        <div className="facility-item" style={{ marginTop: '24px' }}>
          <FaTimesCircle className="facility-icon" />
          <div>
            <h3>Coberturas con las que no trabajamos</h3>
            <p>
              Mi Lugar <strong>no trabaja con {noAtendidasTexto}</strong>. Si tenés alguna de 
              esas coberturas, escribinos igual y te orientamos sobre las alternativas disponibles.
            </p>
          </div>
        </div>
      </section>

      {/* Servicios Cubiertos */}
      <section className="sede-facilities">
        <h2 className="section-title">¿Qué servicios cubren las obras sociales?</h2>
        <div className="sede-info">
          <div className="sede-details">
            <p>
              Las obras sociales brindan cobertura integral para personas con discapacidad según la 
              Ley 24.901. Esto incluye:
            </p>
            <div className="facilities-grid">
              {serviciosCubiertos.map((servicio, index) => (
                <div key={index} className="facility-item">
                  <FaCheckCircle className="facility-icon" />
                  <div>
                    <h3>{servicio}</h3>
                  </div>
                </div>
              ))}
            </div>
            <p>
              <strong>Importante:</strong> La cobertura es del 100% para las prestaciones básicas 
              establecidas por ley, sin costos adicionales para las familias.
            </p>
          </div>
        </div>
      </section>

      {/* Documentación */}
      <section className="sede-facilities">
        <h2 className="section-title">Documentación Necesaria</h2>
        <p>Para iniciar el trámite con tu obra social, necesitarás los siguientes documentos:</p>
        <div className="facilities-grid">
          {documentosNecesarios.map((doc, index) => (
            <div key={index} className="facility-item">
              <FaFileAlt className="facility-icon" />
              <div>
                <h3>{doc.documento}</h3>
                <p>{doc.descripcion}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Proceso de Gestión */}
      <section className="sede-facilities">
        <h2 className="section-title">¿Cómo gestionamos tu cobertura?</h2>
        <div className="facilities-grid">
          {procesoGestion.map((item, index) => (
            <div key={index} className="facility-item">
              <FaCheckCircle className="facility-icon" />
              <div>
                <h3>Paso {item.paso}: {item.titulo}</h3>
                <p>{item.descripcion}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Preguntas Frecuentes */}
      <section className="sede-facilities">
        <h2 className="section-title">Preguntas Frecuentes sobre Obras Sociales</h2>
        <div className="facilities-grid">
          {preguntasFrecuentes.map((item) => (
            <div key={item.pregunta} className="facility-item">
              <FaCheckCircle className="facility-icon" />
              <div>
                <h3>{item.pregunta}</h3>
                <p>{item.respuesta}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* WhatsApp Float Button */}
      <a
        href={SEDES[0].whatsapp}
        className="whatsapp-float"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Escribinos por WhatsApp"
        onClick={(e) => {
          e.preventDefault();
          gtag_report_conversion(SEDES[0].whatsapp);
        }}
      >
        <FaWhatsapp />
      </a>
    </div>
  );
};

export default ObrasSociales;
